import type CreativeSDK from '@cesdk/cesdk-js';
import {
	GQLDesignPageFragment,
	GQLMerchTileFragment,
	GQLOrderItemFragment,
	GQLOrderItemPrintSideType,
	GQLProductFragment,
	GQLProductSideSideType,
} from '@theprintbar/graphql';
import {
	Merch,
	cloneDeep,
	getDesignReadySides,
	getSortedColours,
	getSortedDecorationTypes,
	getSortedMerchDecorations,
	mapEnumValue,
} from '@theprintbar/shared';
import { editorTools } from '../../CreativeEditor/editorTools';
import { handleEditorUpdate } from './handleEditorUpdate';

interface HandleEditorCreateSceneProps {
	editor: CreativeSDK;
	product: GQLProductFragment;
	domain: string;
	merch?: Merch;
	merchItem?: GQLMerchTileFragment | null;
	orderItem?: GQLOrderItemFragment | null;
	content?: GQLDesignPageFragment;
	updateMerch?: (newMerch: Merch) => void;
}

export const handleEditorCreateScene = async ({
	editor,
	product,
	merch,
	orderItem,
	merchItem,
	domain,
	content,
	updateMerch,
}: HandleEditorCreateSceneProps) => {
	try {
		const merchSides = getDesignReadySides(product);
		const usedSides = merchSides.filter(s => {
			if (merchItem) {
				return merchItem?.decorations.some(d => d.side?.side === s.side);
			} else if (orderItem) {
				return orderItem?.prints?.some(p => mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, p.side) === s.side);
			}
			return false;
		});

		//Prefer the side flagged as the main decoration so the editor opens on it. Falls back
		//to the first used side (product-side order) and finally the first product side.
		const mainSide = merchItem?.decorations.find(d => d.isMainDecoration && d.side?.side)?.side?.side as
			| GQLProductSideSideType
			| undefined;
		const activeSide = (mainSide && merchSides.find(s => s.side === mainSide)?.side) || (usedSides[0] || merchSides[0]).side;
		let scene: number;

		const colour =
			getSortedColours(product.colours).find(c => c.colour.code === merch?.colour)?.colour.code ||
			getSortedColours(product.colours)[0].colour.code;

		//Only load from scene if there's no merchItem or if there's a scene file
		if (!!merch?.scene && (!merchItem || !!merchItem.sceneFile)) {
			scene = await editorTools.loadFromScene({ editor, product, merch, domain, content });
			let newMerch = cloneDeep(merch) as Merch;

			if (!orderItem) {
				//Ensure the decorations are available on this product unless its an orderItem
				newMerch.decorations = newMerch.decorations.map(d => {
					const availableDecorations = getSortedDecorationTypes(product.sides.find(s => s.side === d.side));
					return availableDecorations.length
						? {
								...d,
								type: availableDecorations.includes(d.type) ? d.type : availableDecorations[0],
						  }
						: d;
				});
			}

			for (const productSide of merchSides) {
				//Update merch data with each side
				newMerch = await handleEditorUpdate({
					domain,
					activeSide: productSide.side,
					currentMerch: newMerch,
					editor,
					product,
					firstUpdate: true,
				});
			}
			updateMerch?.(newMerch);
		} else {
			scene = editor.engine.scene.create();
			editor.engine.block.setFloat(scene, 'scene/dpi', 300);
			// Sequential (not `forEach(async…)`): CE.SDK auto-lays out sibling pages horizontally
			// in the scene. `addBackgroundToPage` reads `getGlobalBoundingBoxX(page)`, which only
			// stabilises after the current page's layout is settled. Running iterations in parallel
			// meant a later page could shift this one's global X mid-flight — background lands to
			// the left of the artboard. Sequential + awaited setup makes the layout deterministic.
			for (const productSide of merchSides) {
				const page = editorTools.createPageForSide(editor, scene, product, productSide, colour);
				await new Promise(resolve => setTimeout(resolve, 10));
				editorTools.addBackgroundToPage(editor, domain, scene, page, product, productSide, colour);
				await new Promise(resolve => setTimeout(resolve, 10));
				editorTools.addExclusionZonesToPage(editor, productSide, page);

				if (productSide.side === GQLProductSideSideType.Label) {
					editorTools.addNeckLabelBlocks(editor, product);
				}

				if (orderItem) {
					const matchingPrint = orderItem.prints?.find(
						print => mapEnumValue(GQLOrderItemPrintSideType, GQLProductSideSideType, print.side) === productSide.side,
					);

					if (!matchingPrint) continue;
					editorTools.addArtworkToPage({
						editor,
						page,
						side: productSide,
						artwork: matchingPrint.artwork,
						width: matchingPrint.width,
						height: matchingPrint.height,
						left: matchingPrint.left,
						top: matchingPrint.top,
					});
				} else if (merchItem) {
					const matchingDecoration = merchItem.decorations?.find(d => d.side?.side === productSide.side);
					if (!matchingDecoration) continue;
					editorTools.addArtworkToPage({
						editor,
						page,
						side: productSide,
						artwork: matchingDecoration.design?.image,
						width: matchingDecoration.width,
						height: matchingDecoration.height,
						left: matchingDecoration.left,
						top: matchingDecoration.top,
					});
				}
			}
		}

		await new Promise(resolve => setTimeout(resolve, 10));
		await editorTools.switchToSide(editor, activeSide);
		const newHistory = editor.engine.editor.createHistory();
		const oldHistory = editor.engine.editor.getActiveHistory();
		editor.engine.editor.setActiveHistory(newHistory);
		editor.engine.editor.destroyHistory(oldHistory);
		// Push a new state to the undo stack
		editor.engine.editor.addUndoStep();

		return scene;
	} catch (e) {
		console.log(`Failed to load the editor: ${e}`);
	}
};
